import React, { useEffect, useMemo, useState } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import Badge from "@/components/atoms/Badge";
import ExpandIcon from "@/assets/svg/expand.svg";
import { FormStepGroupType } from "@/data/forms/types";

export const FormCollapsibleGroup = ({
  group,
  t,
  formValues,
  errors,
  nextStepTriggerCount = 0,
  children,
}: {
  group: FormStepGroupType;
  t: Record<string, string>;
  formValues: Record<string, unknown>;
  errors?: Record<string, unknown>;
  nextStepTriggerCount?: number;
  children: React.ReactNode;
}) => {
  const hasFieldValues = useMemo(
    () => group.fields.some((field) => Boolean(formValues[field.name])),
    [group.fields, formValues]
  );
  const groupHasErrors = group.fields.some((field) => Boolean(errors?.[field.name]));
  const forceOpen = groupHasErrors && nextStepTriggerCount > 0;
  const [isOpen, setIsOpen] = useState(hasFieldValues);
  const open = isOpen || forceOpen;

  useEffect(() => {
    if (hasFieldValues) setIsOpen(true);
  }, [hasFieldValues]);

  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen, nextStepTriggerCount]);

  return (
    <Collapsible.Root
      key={forceOpen ? `${group.name}-error-${nextStepTriggerCount}` : group.name}
      className="Step__collapsible"
      data-state={open ? "open" : "closed"}
      open={open}
      onOpenChange={setIsOpen}>
      <Collapsible.Trigger
        className="Step__collapsible-trigger"
        data-qa={`collapsible-${group.name}`}>
        <div className="Step__collapsible-trigger-text">
          <div className="Step__collapsible-trigger-title-row">
            {group.title && (
              <span className="Step__collapsible-trigger-title">{t[group.title]}</span>
            )}
            {group.optional && <Badge variant="tertiaryCompact">{t["OPTIONAL"]}</Badge>}
          </div>
          {group.subTitle && (
            <p className="Step__collapsible-trigger-subtitle">{t[group.subTitle]}</p>
          )}
        </div>
        <span
          className="Step__collapsible-trigger-icon-wrapper"
          aria-hidden>
          <ExpandIcon className="Step__collapsible-trigger-icon" />
        </span>
      </Collapsible.Trigger>

      <Collapsible.Content className="Step__collapsible-content">{children}</Collapsible.Content>
    </Collapsible.Root>
  );
};
